import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase';
import { companyAccess, blockedMessage } from './access';
import { authFlows } from './authFlows';

import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import EmployeeSchedule from './pages/EmployeeSchedule';
import Timesheets from './pages/Timesheets';
import Shifts from './pages/Shifts';
import Schedule from './pages/Schedule';
import Settings from './pages/Settings';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Message shown on the login screen when a sign-in is rejected here (not an
  // admin, or the company is locked / trial-expired). The Login page itself
  // only reports bad credentials — all access checks live here, so there's no
  // race between two places signing the user out.
  const [authMessage, setAuthMessage] = useState(null);

  useEffect(() => {
    // Live listener on the admin's company doc. Kept around so we can detach it
    // when the admin signs out or the auth state changes.
    let unsubCompany = null;
    const detachCompany = () => {
      if (unsubCompany) {
        unsubCompany();
        unsubCompany = null;
      }
    };

    const reject = (message) => {
      detachCompany();
      setUser(null);
      setAuthMessage(message);
      if (auth.currentUser) auth.signOut();
      setLoading(false);
    };

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Registration in progress: the user/company docs aren't written yet, so
      // checking them now would wrongly reject the brand-new account. Register
      // clears the flag and re-signs-in once the docs exist.
      if (authFlows.registering) return;
      detachCompany();
      if (!firebaseUser) {
        // Signed out. Leave authMessage intact so a rejection message set just
        // before signOut() stays visible on the login screen.
        setUser(null);
        setLoading(false);
        return;
      }

      let userDoc;
      try {
        userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      } catch (e) {
        // The user doc was deleted out from under us → rules deny the read and
        // it throws. Treat as a removed account instead of hanging on "Loading".
        reject('This account no longer exists. Please contact support.');
        return;
      }
      if (!userDoc.exists() || userDoc.data().role !== 'admin') {
        reject('This account no longer exists. Please contact support.');
        return;
      }

      const { companyId } = userDoc.data();
      if (!companyId) {
        setUser(firebaseUser);
        setAuthMessage(null);
        setLoading(false);
        return;
      }

      // Watch the company in real time: the first snapshot gates this load
      // (locked/trial-expired → rejected), and any later lock by the superadmin
      // signs the admin out immediately, no refresh.
      // includeMetadataChanges so a server-confirmed snapshot always arrives,
      // even when the data is unchanged (e.g. retrying while still suspended).
      unsubCompany = onSnapshot(doc(db, 'companies', companyId), { includeMetadataChanges: true }, (snap) => {
        // Self-deletion in progress: the docs vanish one by one by design.
        // Don't bounce — deleteUser() will end the session when it's done.
        if (authFlows.deleting) return;
        // A missing company doc means the company was deleted → treat as blocked.
        const access = !snap.exists()
          ? { blocked: true, reason: 'deleted' }
          : companyAccess(snap.data());
        if (access.blocked) {
          // Only bounce on a server-confirmed state. Firestore delivers cached
          // data first, which can still say "locked" right after a superadmin
          // unlock — acting on it would wrongly reject a now-active company
          // until a full page refresh clears the cache.
          if (snap.metadata.fromCache) return;
          reject(access.reason === 'deleted'
            ? 'This account no longer exists. Please contact support.'
            : blockedMessage(access.reason));
        } else {
          setUser(firebaseUser);
          setAuthMessage(null);
          setLoading(false);
        }
      }, () => {
        // Listener errored (e.g. permissions revoked) — fail safe to login,
        // unless we're mid self-deletion (own user doc goes away → denied).
        if (authFlows.deleting) return;
        reject('This account no longer exists. Please contact support.');
      });
    });

    return () => {
      detachCompany();
      unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', backgroundColor: '#0f0f0f', color: '#fff', fontSize: 18
      }}>
        Loading...
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Both auth routes render the same AuthPage so switching between
            them animates in place instead of remounting the whole page. */}
        <Route path="/login" element={!user ? <AuthPage mode="login" authMessage={authMessage} clearAuthMessage={() => setAuthMessage(null)} /> : <Navigate to="/" />} />
        <Route path="/register" element={!user ? <AuthPage mode="register" authMessage={authMessage} clearAuthMessage={() => setAuthMessage(null)} /> : <Navigate to="/" />} />
        <Route path="/" element={user ? <Dashboard /> : <Navigate to="/login" />} />
        <Route path="/employees" element={user ? <Employees /> : <Navigate to="/login" />} />
        <Route path="/employees/:id" element={user ? <EmployeeSchedule /> : <Navigate to="/login" />} />
        <Route path="/timesheets" element={user ? <Timesheets /> : <Navigate to="/login" />} />
        <Route path="/shifts" element={user ? <Shifts /> : <Navigate to="/login" />} />
        <Route path="/schedule" element={user ? <Schedule /> : <Navigate to="/login" />} />
        <Route path="/settings" element={user ? <Settings /> : <Navigate to="/login" />} />
      </Routes>
    </Router>
  );
}

export default App;